// src/components/minimap/MinimapGameLink.jsx
// A small map chip for a game row anywhere in the dashboard: bright when
// Minimap Lab already has that game, faint when it does not (a click then
// makes the match from the scrim log). Games are known by date, opponent and
// number in the day's series — numbered by gameNumbers() in sheet order.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Map as MapIcon } from "lucide-react";
import { seriesKey } from "../../utils/minimap/series";

const up = (s) => String(s || "").trim().toUpperCase();

/** Each game's number in its day's series against the same opponent, in the order given: Map(game → n). */
export function gameNumbers(games = []) {
  const seen = new Map();
  const out = new Map();
  games.forEach((g) => {
    const k = seriesKey(g);
    const n = (seen.get(k) || 0) + 1;
    seen.set(k, n);
    out.set(g, n);
  });
  return out;
}

/** The games Minimap Lab has, as "YYYYMMDD|OPPONENT|n" keys (read once from the browser's library). */
export function useMinimapIndex() {
  const [matches, setMatches] = useState([]);
  useEffect(() => {
    let alive = true;
    // Loaded on demand: the dashboard's first screen does not carry the Lab's code.
    import("../../utils/minimap/store")
      .then((store) => store.listMatches())
      .then((list) => alive && setMatches(list))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return useMemo(() => {
    const keys = new Set();
    matches.forEach((m) => {
      if (m.meta.sheetKey) keys.add(m.meta.sheetKey);
      else if (m.meta.date && m.meta.opponent && m.meta.gameNo) keys.add(`${m.meta.date.replace(/-/g, "")}|${up(m.meta.opponent)}|${m.meta.gameNo}`);
    });
    return keys;
  }, [matches]);
}

export default function MinimapGameLink({ game, n, index }) {
  const navigate = useNavigate();
  if (!game?.Date || !game?.competitor || !n) return null;
  const has = index?.has(`${game.Date}|${up(game.competitor)}|${n}`);
  const side = String(game.side || game.Side || "").toLowerCase();
  const go = (e) => {
    e.stopPropagation();
    const r = Number(game.win) === 1 ? "win" : Number(game.lose) === 1 ? "loss" : "";
    const q = new URLSearchParams({ date: String(game.Date), opp: String(game.competitor), g: String(n), side, r, type: String(game.type || "") });
    navigate(`/minimap-lab?${q.toString()}`);
  };
  return (
    <button
      type="button"
      onClick={go}
      title={has ? "เปิดใน Minimap Lab" : "สร้างแมตช์ใน Minimap Lab จากเกมนี้"}
      aria-label={has ? "เปิดใน Minimap Lab" : "สร้างแมตช์ใน Minimap Lab"}
      style={{
        appearance: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        height: 20,
        marginLeft: 6,
        padding: "0 6px",
        borderRadius: 6,
        border: `1px solid ${has ? "var(--accent-line)" : "var(--border-2)"}`,
        background: has ? "var(--accent-soft)" : "transparent",
        color: has ? "var(--accent-ink)" : "var(--text-3)",
        cursor: "pointer",
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        verticalAlign: "middle",
      }}
    >
      <MapIcon size={12} strokeWidth={2.2} />
      {has ? <Check size={11} strokeWidth={2.8} /> : null}
    </button>
  );
}
